import axiosInstance from './axiosConfig';

/**
 * aiApi.js
 * Client helpers for the AI Assistant. Buffered tasks use the shared axios
 * instance; streaming tasks use fetch + ReadableStream to consume Server-Sent
 * Events (EventSource cannot send Authorization headers or POST bodies).
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Reduce a mail object to just the fields the AI needs (avoid sending internal
 * flags/ids). Prefers plain text body over HTML to save tokens.
 */
export function toAiEmail(mail = {}) {
  if (!mail) return {};
  return {
    from: mail.from || '',
    to: mail.to || '',
    subject: mail.subject || '',
    date: mail.createdAt || mail.date || '',
    body: String(mail.text || mail.body || stripHtml(mail.html) || '').slice(0, 20000),
  };
}

function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

// ---- Buffered task calls ---------------------------------------------------

export const aiApi = {
  summary: (email) => post('/api/ai/summary', { email }),
  reply: (email, opts) => post('/api/ai/reply', { email, ...opts }),
  translate: (email, opts) => post('/api/ai/translate', { email, ...opts }),
  rewrite: (email, opts) => post('/api/ai/rewrite', { email, ...opts }),
  grammar: (email, text) => post('/api/ai/grammar', { email, text }),
  security: (email) => post('/api/ai/security', { email }),
  tasks: (email) => post('/api/ai/tasks', { email }),
  meeting: (email) => post('/api/ai/meeting', { email }),
  calendar: (email) => post('/api/ai/calendar', { email }),
  attachment: (payload) => post('/api/ai/attachment', payload),
  subject: (email, text) => post('/api/ai/subject', { email, text }),
  sentiment: (email) => post('/api/ai/sentiment', { email }),
  compose: (payload) => post('/api/ai/compose', payload),
  improve: (payload) => post('/api/ai/improve', payload),
  continueWriting: (payload) => post('/api/ai/continue', payload),
  getSettings: () => get('/api/ai/settings'),
  getHistory: (limit = 25) => get(`/api/ai/history?limit=${limit}`),
  getAdminSettings: () => get('/api/ai/admin/settings'),
  updateAdminSettings: (payload) => put('/api/ai/admin/settings', payload),
};

async function post(url, body) {
  const res = await axiosInstance.post(url, body);
  return res.data;
}
async function get(url) {
  const res = await axiosInstance.get(url);
  return res.data;
}
async function put(url, body) {
  const res = await axiosInstance.put(url, body);
  return res.data;
}

// ---- Streaming (SSE over fetch) --------------------------------------------

/**
 * Stream an AI task. Calls handlers as events arrive.
 * @returns {() => void} abort function
 */
export function streamAiTask(task, payload, { onMeta, onDelta, onDone, onError } = {}) {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai/stream/${task}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        let message = `Request failed (${response.status})`;
        try {
          const data = await response.json();
          message = data.message || message;
        } catch (_) { /* ignore */ }
        onError?.(new Error(message));
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line.
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const raw of events) {
          const { event, data } = parseSseEvent(raw);
          if (!data) continue;
          let json;
          try { json = JSON.parse(data); } catch (_) { continue; }
          if (event === 'meta') onMeta?.(json);
          else if (event === 'delta') onDelta?.(json.text || '');
          else if (event === 'done') onDone?.(json);
          else if (event === 'error') onError?.(new Error(json.message || 'AI error'));
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') onError?.(error);
    }
  })();

  return () => controller.abort();
}

function parseSseEvent(raw) {
  let event = 'message';
  const dataLines = [];
  raw.split('\n').forEach((line) => {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });
  return { event, data: dataLines.join('\n') };
}

export default aiApi;
